import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewFilters from '@app/src/components/Drawer/Views/DrawerViewFilters'
import { OptionIcon } from '@app/src/components/Form/Select'
import { useAutocompleteStyles } from '@app/src/components/Form/Select/SimpleSelect'
import { FinalRiskRatingIcon } from '@app/src/components/Table/Cells/FinalRiskRatingCell'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import CustomCategoriesFilters from '@app/src/pages/ResourceCollection/Filters/CustomCategoriesFilters'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterFacetSelect from '@app/src/pages/ResourceCollection/Filters/FilterFacetSelect'
import type { FiltersContextProps } from '@app/src/pages/ResourceCollection/Filters/Filters'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import FilterSection from '@app/src/pages/ResourceCollection/Filters/FilterSection'
import { PeriodFilterOptions } from '@app/src/pages/ResourceCollection/Filters/PeriodFilterDisplay'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import {
  Operators,
  RESPONSE_SCRAPING_LATEST_SUBMITTED_FILTER,
} from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResetPage } from '@app/src/types/filter'
import { SortOrder } from '@app/src/types/filter'
import { Solutions } from '@app/src/wf-constants'
import { Box, Button, Chip, createFilterOptions, Grid, Tooltip } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'
import type { FacetItem } from '..'
import AllFiltersButton from './AllFiltersButton'
import AssessmentTemplateSelectorWithLevelFilter from './AssessmentMiniFilter/AssessmentTemplateSelectorWithLevelFilter'
import useLocalisedCountryFilter from './Common/useLocalisedCountryFilter'
import OptionWithLabel from './OptionWithLabel'
import ProductMappingFilters from './ProductMappingFilters'
import RangeFilter from './RangeFilter'
import SpendFilter from './SpendFilter'
import StatusOption from './StatusOption'

interface ResponseFiltersProps {
  implicitFilters?: FilterGroup[]
  allowedFilters: FiltersContextProps['allowedFilters']
  resetPage?: ResetPage
}

const ResponsesScrapingFilters = ({
  allowedFilters,
  resetPage,
  implicitFilters: baseImplicitFilters = [],
}: ResponseFiltersProps): JSX.Element => {
  const { renderWithPermission } = usePermissions()
  const autocompleteClasses = useAutocompleteStyles()
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const verificationEnabled = hasPermission(Permissions.WRITE_VERIFICATION)
  const { solution } = useAuthentication().scope
  const { openDrawer } = useDrawer()
  const canAccessAssessmentTemplate = hasPermission(Permissions.ASSESSMENT_TEMPLATE_ACCESS)
  const implicitFilters = [RESPONSE_SCRAPING_LATEST_SUBMITTED_FILTER, ...baseImplicitFilters]
  const { renderOption, renderTags, customFilterOption, customSort } = useLocalisedCountryFilter()
  const { productMapping } = useFlags()

  const tierFilter = createFilterOptions<FacetItem>({
    stringify: option =>
      formatMessage(
        {
          id: `schemas.provider.tierValues`,
        },
        { tier: option.value },
      ),
  })

  return (
    <Grid container spacing={1}>
      <Filters allowedFilters={allowedFilters} resetPage={resetPage}>
        {({ filters, clearQueryFilter, inDrawerFiltersCount }) => {
          const isFiltered = Object.keys(filters).length > 0
          return (
            <>
              <Grid item md={3} sm={10}>
                <Filter name="provider.id" operator={Operators.In} isQuickFilter>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      implicitFilters={implicitFilters}
                      size="small"
                      multiple
                      facetsParam={{
                        key: [FetchKey.ResponseScrapingFacets, filterName],
                        endpoint: endpoints.responseScrapingFacet,
                        facetsParam: [{ name: 'provider.name' }],
                        sort: { target: 'label', order: SortOrder.ASCENDING },
                      }}
                      filterName={filterName}
                      onChange={value => onChange(value)}
                      fieldLabel={formatMessage({ id: 'schemas.filter.companyName' })}
                      value={[value].flat()}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item md={3} sm={10}>
                <Filter name="questionnaireTemplate.id" operator={Operators.In} isQuickFilter>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      implicitFilters={implicitFilters}
                      size="small"
                      multiple
                      facetsParam={{
                        key: [FetchKey.ResponseScrapingFacets, filterName],
                        endpoint: endpoints.responseScrapingFacet,
                        facetsParam: [{ name: 'questionnaireTemplate.title' }],
                        sort: { target: 'label', order: SortOrder.ASCENDING },
                      }}
                      filterName={filterName}
                      onChange={onChange}
                      fieldLabel={formatMessage({ id: 'schemas.filter.requestName' })}
                      value={[value].flat()}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item md={2}>
                <Filter name="periodName" operator={Operators.In} isQuickFilter>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      implicitFilters={implicitFilters}
                      multiple
                      facetsParam={{
                        key: [FetchKey.ResponseScrapingFacets, filterName],
                        endpoint: endpoints.responseScrapingFacet,
                        facetsParam: [{ name: filterName, isEnum: true }],
                        sort: { target: 'label', order: SortOrder.DESCENDING },
                      }}
                      filterName={filterName}
                      onChange={value => onChange(value)}
                      fieldLabel={formatMessage({ id: 'schemas.request.period' })}
                      value={[value].flat()}
                      {...PeriodFilterOptions}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item md={2}>
                <Box display="inline-block">
                  <AllFiltersButton
                    inDrawerFiltersCount={inDrawerFiltersCount}
                    openDrawer={() =>
                      openDrawer(
                        <DrawerViewFilters allowedFilters={allowedFilters}>
                          <Box px={2} mt={2}>
                            <Filter name="periodName" operator={Operators.In}>
                              {({ value, onChange, filterName }) => (
                                <FilterFacetSelect
                                  implicitFilters={implicitFilters}
                                  size="small"
                                  multiple
                                  facetsParam={{
                                    key: [FetchKey.ResponseScrapingFacets, filterName],
                                    endpoint: endpoints.responseScrapingFacet,
                                    facetsParam: [{ name: filterName, isEnum: true }],
                                    sort: { target: 'label', order: SortOrder.DESCENDING },
                                  }}
                                  filterName={filterName}
                                  onChange={value => onChange(value)}
                                  fieldLabel={formatMessage({ id: 'schemas.request.period' })}
                                  value={[value].flat()}
                                  {...PeriodFilterOptions}
                                />
                              )}
                            </Filter>
                            <FilterSection label={formatMessage({ id: 'schemas.filter.responses' })}>
                              <Filter name="questionnaireTemplate.id" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseScrapingFacets, filterName],
                                      endpoint: endpoints.responseScrapingFacet,
                                      facetsParam: [{ name: 'questionnaireTemplate.title' }],
                                    }}
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.filter.requestName' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                            </FilterSection>
                            <FilterSection label={formatMessage({ id: 'schemas.filter.companyDetails' })}>
                              <Box mb={2}>
                                <Filter name="provider.id" operator={Operators.In}>
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseScrapingFacets, filterName],
                                        endpoint: endpoints.responseScrapingFacet,
                                        facetsParam: [{ name: 'provider.id' }],
                                        sort: {
                                          order: SortOrder.ASCENDING,
                                          target: 'label',
                                        },
                                      }}
                                      filterName={filterName}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.filter.companyId' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </Box>
                              <Box mb={2}>
                                <Filter name="provider.countryId" operator={Operators.In}>
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseScrapingFacets, filterName],
                                        endpoint: endpoints.responseScrapingFacet,
                                        facetsParam: [{ name: 'provider.country.iso3166Alpha2' }],
                                      }}
                                      renderOption={renderOption}
                                      renderTags={renderTags}
                                      filterName={filterName}
                                      filterOptions={customFilterOption}
                                      customSort={customSort}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.filter.country.countryCode' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </Box>
                              <Box mb={2}>
                                <Filter name="provider.id" operator={Operators.In}>
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseScrapingFacets, filterName],
                                        endpoint: endpoints.responseScrapingFacet,
                                        facetsParam: [{ name: 'provider.name' }],
                                        sort: { target: 'label', order: SortOrder.ASCENDING },
                                      }}
                                      filterName={filterName}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.filter.companyName' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </Box>
                            </FilterSection>
                            <FilterSection label={formatMessage({ id: 'schemas.filter.responseDetails' })}>
                              {verificationEnabled && (
                                <>
                                  <Box mb={2}>
                                    <Filter
                                      name="response.verifications.internalVerificationStatus"
                                      operator={Operators.In}
                                    >
                                      {({ value, onChange, filterName }) => (
                                        <FilterFacetSelect
                                          implicitFilters={implicitFilters}
                                          size="small"
                                          multiple
                                          facetsParam={{
                                            key: [FetchKey.ResponseScrapingFacets, filterName],
                                            endpoint: endpoints.responseScrapingFacet,
                                            facetsParam: [{ name: filterName }],
                                          }}
                                          renderOption={(props, option, { selected }): JSX.Element => (
                                            <StatusOption
                                              key={option.label}
                                              props={props}
                                              option={option}
                                              selected={selected}
                                              verification
                                            />
                                          )}
                                          filterName={filterName}
                                          onChange={value => onChange(value)}
                                          fieldLabel={formatMessage({
                                            id: 'filters.responseItem.internalVerificationStatus',
                                          })}
                                          value={[value].flat()}
                                          renderTags={(value, getTagProps) =>
                                            value?.map(
                                              (option, index) =>
                                                option && (
                                                  <Tooltip
                                                    key={option?.value?.toString() || index}
                                                    title={formatMessage({
                                                      id: `reporting.internalVerificationStatuses.${option.label}`,
                                                    })}
                                                    classes={{ tooltipPlacementBottom: autocompleteClasses.tooltip }}
                                                    arrow
                                                  >
                                                    <Chip
                                                      label={formatMessage({
                                                        id: `reporting.internalVerificationStatuses.${option.label}`,
                                                      })}
                                                      size="small"
                                                      {...getTagProps({ index })}
                                                    />
                                                  </Tooltip>
                                                ),
                                            )
                                          }
                                          skipNotSet={false}
                                        />
                                      )}
                                    </Filter>
                                  </Box>
                                </>
                              )}
                              <RangeFilter
                                uniqueIdFrom="responseDateFrom"
                                uniqueIdTo="responseDateTo"
                                name="startedAt"
                                fieldLabelFrom={formatMessage({ id: 'schemas.response.responseDateFrom' })}
                                fieldLabelTo={formatMessage({ id: 'schemas.response.responseDateTo' })}
                              />
                            </FilterSection>
                            <SpendFilter spendFilterNamePrefix="provider" defaultCollapsed />
                            {canAccessAssessmentTemplate && (
                              <AssessmentTemplateSelectorWithLevelFilter
                                fetchKey={FetchKey.ResponseScraping}
                                facetEndpoint={endpoints.responseScrapingFacet}
                                allowedFilters={allowedFilters}
                                assessmentFilterNamePrefix="provider.assessments"
                              />
                            )}
                            <FilterSection
                              defaultCollapsed
                              label={formatMessage({ id: 'schemas.filter.additionalInformation' })}
                            >
                              <Box mb={2}>
                                <Filter operator={Operators.In} name="provider.providerOwners.userId">
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseScrapingFacets, filterName],
                                        endpoint: endpoints.responseScrapingFacet,
                                        facetsParam: [{ name: 'provider.providerOwners.user.name' }],
                                        sort: { target: 'label', order: SortOrder.ASCENDING },
                                      }}
                                      filterName={filterName}
                                      onChange={onChange}
                                      fieldLabel={formatMessage({ id: 'schemas.provider.ownerUserIds' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </Box>

                              <Box mb={2}>
                                <Filter operator={Operators.In} name="provider.finalRiskRating">
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseScrapingFacets, filterName],
                                        endpoint: endpoints.responseScrapingFacet,
                                        facetsParam: [
                                          {
                                            name: 'provider.finalRiskRating',
                                            isEnum: true,
                                          },
                                        ],
                                      }}
                                      renderOption={(props, option, { selected }): JSX.Element => (
                                        <li {...props}>
                                          <OptionIcon selected={selected} multiple />
                                          <FinalRiskRatingIcon value={option?.label} />
                                          {option?.label
                                            ? formatMessage({
                                                id: `schemas.provider.finalRiskRatingValues.${option.label}`,
                                              })
                                            : ''}
                                        </li>
                                      )}
                                      renderTags={(value, getTagProps) =>
                                        value?.map(
                                          (option, index) =>
                                            option && (
                                              <Chip
                                                {...getTagProps({ index })}
                                                key={option?.value?.toString() || index}
                                                icon={<FinalRiskRatingIcon value={option.label} />}
                                                label={formatMessage({
                                                  id: `schemas.provider.finalRiskRatingValues.${option.label}`,
                                                })}
                                                size="small"
                                              />
                                            ),
                                        )
                                      }
                                      filterName={filterName}
                                      onChange={onChange}
                                      fieldLabel={formatMessage({ id: 'schemas.provider.finalRiskRating' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </Box>
                              <Box mb={2}>
                                <Filter operator={Operators.In} name="provider.priority">
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseScrapingFacets, filterName],
                                        endpoint: endpoints.responseScrapingFacet,
                                        facetsParam: [{ name: 'provider.priority', isEnum: true }],
                                      }}
                                      renderOption={(props, option, { selected }) => (
                                        <OptionWithLabel
                                          key={option.label}
                                          props={props}
                                          label={formatMessage({
                                            id: option?.label
                                              ? `schemas.provider.priorityValues.${option.label}`
                                              : 'general.notAvailable',
                                          })}
                                          multiple
                                          selected={selected}
                                        />
                                      )}
                                      renderTags={(value, getTagProps) =>
                                        value?.map(
                                          (option, index) =>
                                            option && (
                                              <Chip
                                                {...getTagProps({ index })}
                                                key={option?.value?.toString() || index}
                                                label={formatMessage({
                                                  id: `schemas.provider.priorityValues.${option.label}`,
                                                })}
                                                size="small"
                                              />
                                            ),
                                        )
                                      }
                                      filterName={filterName}
                                      onChange={onChange}
                                      fieldLabel={formatMessage({ id: 'schemas.provider.priority' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </Box>
                              {solution !== Solutions.Finance && (
                                <>
                                  <Box mb={2}>
                                    <Filter operator={Operators.In} name="provider.activityStatus">
                                      {({ value, onChange, filterName }) => (
                                        <FilterFacetSelect
                                          implicitFilters={implicitFilters}
                                          size="small"
                                          multiple
                                          facetsParam={{
                                            key: [FetchKey.ResponseScrapingFacets, filterName],
                                            endpoint: endpoints.responseScrapingFacet,
                                            facetsParam: [
                                              {
                                                name: 'provider.activityStatus',
                                                isEnum: true,
                                              },
                                            ],
                                          }}
                                          renderOption={(props, option, { selected }) => (
                                            <OptionWithLabel
                                              key={option.label}
                                              props={props}
                                              label={formatMessage({
                                                id: option?.label
                                                  ? `schemas.provider.activityStatusValues.${option.label}`
                                                  : 'general.notAvailable',
                                              })}
                                              multiple
                                              selected={selected}
                                            />
                                          )}
                                          renderTags={(value, getTagProps) =>
                                            value?.map(
                                              (option, index) =>
                                                option && (
                                                  <Chip
                                                    {...getTagProps({ index })}
                                                    key={option?.value?.toString() || index}
                                                    label={formatMessage({
                                                      id: `schemas.provider.activityStatusValues.${option.label}`,
                                                    })}
                                                    size="small"
                                                  />
                                                ),
                                            )
                                          }
                                          filterName={filterName}
                                          onChange={onChange}
                                          fieldLabel={formatMessage({ id: 'schemas.provider.activityStatus' })}
                                          value={[value].flat()}
                                        />
                                      )}
                                    </Filter>
                                  </Box>
                                  <Box mb={2}>
                                    <Filter operator={Operators.In} name="provider.providerApprovalStatus">
                                      {({ value, onChange, filterName }) => (
                                        <FilterFacetSelect
                                          implicitFilters={implicitFilters}
                                          size="small"
                                          multiple
                                          facetsParam={{
                                            key: [FetchKey.ResponseScrapingFacets, filterName],
                                            endpoint: endpoints.responseScrapingFacet,
                                            facetsParam: [
                                              {
                                                name: 'provider.providerApprovalStatus',
                                                isEnum: true,
                                              },
                                            ],
                                          }}
                                          renderOption={(props, option, { selected }) => (
                                            <OptionWithLabel
                                              key={option.label}
                                              props={props}
                                              label={formatMessage({
                                                id: option?.label
                                                  ? `schemas.provider.providerApprovalStatusValues.${option.label}`
                                                  : 'general.notAvailable',
                                              })}
                                              multiple
                                              selected={selected}
                                            />
                                          )}
                                          renderTags={(value, getTagProps) =>
                                            value?.map(
                                              (option, index) =>
                                                option && (
                                                  <Chip
                                                    {...getTagProps({ index })}
                                                    key={option?.value?.toString() || index}
                                                    label={formatMessage({
                                                      id: `schemas.provider.providerApprovalStatusValues.${option.label}`,
                                                    })}
                                                    size="small"
                                                  />
                                                ),
                                            )
                                          }
                                          filterName={filterName}
                                          onChange={onChange}
                                          fieldLabel={formatMessage({ id: 'schemas.provider.providerApprovalStatus' })}
                                          value={[value].flat()}
                                        />
                                      )}
                                    </Filter>
                                  </Box>
                                </>
                              )}
                              {renderWithPermission({
                                [Permissions.SOURCING_USER]: (
                                  <>
                                    <Box mb={2}>
                                      <Filter operator={Operators.In} name="provider.supplierUsage">
                                        {({ value, onChange, filterName }) => (
                                          <FilterFacetSelect
                                            implicitFilters={implicitFilters}
                                            size="small"
                                            multiple
                                            facetsParam={{
                                              key: [FetchKey.ResponseScrapingFacets, filterName],
                                              endpoint: endpoints.responseScrapingFacet,
                                              facetsParam: [
                                                {
                                                  name: 'provider.supplierUsage',
                                                  isEnum: true,
                                                },
                                              ],
                                            }}
                                            renderOption={(props, option, { selected }) => (
                                              <OptionWithLabel
                                                key={option.label}
                                                props={props}
                                                label={formatMessage({
                                                  id: option?.label
                                                    ? `schemas.provider.supplierUsageValues.${option.label}`
                                                    : 'general.notAvailable',
                                                })}
                                                multiple
                                                selected={selected}
                                              />
                                            )}
                                            renderTags={(value, getTagProps) =>
                                              value?.map(
                                                (option, index) =>
                                                  option && (
                                                    <Chip
                                                      {...getTagProps({ index })}
                                                      key={option?.value?.toString() || index}
                                                      label={formatMessage({
                                                        id: `schemas.provider.supplierUsageValues.${option.label}`,
                                                      })}
                                                      size="small"
                                                    />
                                                  ),
                                              )
                                            }
                                            filterName={filterName}
                                            onChange={onChange}
                                            fieldLabel={formatMessage({ id: 'schemas.provider.supplierUsage' })}
                                            value={[value].flat()}
                                          />
                                        )}
                                      </Filter>
                                    </Box>
                                    <Box mb={2}>
                                      <Filter operator={Operators.In} name="provider.tier">
                                        {({ value, onChange, filterName }) => (
                                          <FilterFacetSelect
                                            implicitFilters={implicitFilters}
                                            size="small"
                                            multiple
                                            facetsParam={{
                                              key: [FetchKey.ResponseScrapingFacets, filterName],
                                              endpoint: endpoints.responseScrapingFacet,
                                              facetsParam: [{ name: 'provider.tier', isEnum: true }],
                                            }}
                                            filterOptions={(options, state) => [...tierFilter(options, state)]}
                                            renderOption={(props, option, { selected }) => (
                                              <OptionWithLabel
                                                key={option.label}
                                                props={props}
                                                label={formatMessage(
                                                  {
                                                    id: `schemas.provider.tierValues`,
                                                  },
                                                  { tier: option?.label ?? '' },
                                                )}
                                                multiple
                                                selected={selected}
                                              />
                                            )}
                                            renderTags={(value, getTagProps) =>
                                              value?.map(
                                                (option, index) =>
                                                  option && (
                                                    <Chip
                                                      {...getTagProps({ index })}
                                                      key={option?.value?.toString() || index}
                                                      label={formatMessage(
                                                        {
                                                          id: `schemas.provider.tierValues`,
                                                        },
                                                        { tier: option.label },
                                                      )}
                                                      size="small"
                                                    />
                                                  ),
                                              )
                                            }
                                            filterName={filterName}
                                            onChange={onChange}
                                            fieldLabel={formatMessage({ id: 'schemas.provider.tier' })}
                                            value={[value].flat()}
                                          />
                                        )}
                                      </Filter>
                                    </Box>
                                  </>
                                ),
                              })}
                            </FilterSection>
                            <CustomCategoriesFilters baseFilterName="provider" />

                            {productMapping && (
                              <ProductMappingFilters
                                fetchKey={FetchKey.ResponseScrapingFacets}
                                endpoint={endpoints.responseScrapingFacet}
                                filterNamePrefix="provider.mappingNodes"
                                implicitFilters={implicitFilters}
                              />
                            )}
                          </Box>
                        </DrawerViewFilters>,
                      )
                    }
                  />
                </Box>

                {isFiltered && (
                  <Button variant="text" onClick={clearQueryFilter} sx={{ ml: 1 }}>
                    {formatMessage({ id: 'filters.clearAll' })}
                  </Button>
                )}
              </Grid>
            </>
          )
        }}
      </Filters>
    </Grid>
  )
}

export default ResponsesScrapingFilters
